/**
 * NOS-96: AI Command Bar — Section 14 of docs/specs/BUILDER_DESIGN_SPEC.md.
 * Command-palette AI bar at the bottom of the builder. UI shell only.
 */
import { useRef, useEffect } from "react"
import { toast } from "sonner"
import type { PageSection } from "@/templates/types"

const SUGGESTION_CHIPS = [
  "Add testimonials",
  "Improve hero copy",
  "Add volunteer signup",
  "Write mission statement",
  "Add event calendar",
] as const

/** Working chips that trigger addSection */
const WORKING_CHIPS = {
  "Add testimonials": "testimonials" as const,
  "Add volunteer signup": "volunteer_signup" as const,
}

export interface AICommandBarProps {
  primaryColor: string
  sections: PageSection[]
  onAddSection: (sectionType: string, afterIndex: number) => Promise<string | null>
  onClose: () => void
}

/**
 * Compute insert index: add before footer (footer is always last per Rule #5).
 */
function getInsertBeforeFooter(sections: PageSection[]): number {
  const sorted = [...sections].sort((a, b) => a.order_index - b.order_index)
  const footerIdx = sorted.findIndex((s) => s.section_type === "footer")
  if (footerIdx >= 0) return footerIdx - 1
  return Math.max(0, sorted.length - 1)
}

export function AICommandBar({
  primaryColor,
  sections,
  onAddSection,
  onClose,
}: AICommandBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleChipClick = async (chip: (typeof SUGGESTION_CHIPS)[number]) => {
    const sectionType = WORKING_CHIPS[chip as keyof typeof WORKING_CHIPS]
    if (sectionType) {
      const afterIndex = getInsertBeforeFooter(sections)
      const newId = await onAddSection(sectionType, afterIndex)
      if (newId) onClose()
    } else {
      toast.info("AI features coming soon")
    }
  }

  const handleGo = () => {
    toast.info("AI features coming soon")
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleGo()
    }
  }

  return (
    <div
      className="animate-slideUp"
      style={{
        position: "fixed",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        width: 520,
        maxWidth: "calc(100% - 48px)",
        zIndex: 200,
      }}
    >
      <div
        style={{
          background: "rgba(12,12,16,0.98)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow: `0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px ${primaryColor}18`,
          padding: "5px 5px 5px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ color: primaryColor, fontSize: 14, flexShrink: 0 }}>✦</span>
        <input
          ref={inputRef}
          type="text"
          placeholder="Describe a change… e.g. 'Add a testimonials section after mission'"
          onKeyDown={handleKeyDown}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#fff",
            fontSize: 13,
            padding: "10px 0",
            fontFamily: "inherit",
          }}
        />
        <button
          type="button"
          onClick={handleGo}
          style={{
            padding: "9px 18px",
            background: primaryColor,
            border: "none",
            borderRadius: 8,
            color: "#fff",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Go
        </button>
      </div>

      {/* Suggestion chips - Section 14 */}
      <div
        style={{
          display: "flex",
          gap: 5,
          justifyContent: "center",
          marginTop: 8,
          flexWrap: "wrap",
        }}
      >
        {SUGGESTION_CHIPS.map((chip) => (
          <button
            key={chip}
            type="button"
            onClick={() => void handleChipClick(chip)}
            style={{
              padding: "5px 10px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.05)",
              borderRadius: 6,
              color: "rgba(255,255,255,0.35)",
              fontSize: 10,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {chip}
          </button>
        ))}
      </div>
    </div>
  )
}
